import React, { useState, useEffect } from "react";
// Neutral grey placeholder for the empty profile-photo state (no running-man,
// no dead remote service).
import { GREY_PLACEHOLDER as defaultImage } from "../../constants/imageFallback";
import { FaCalendarAlt, FaInfoCircle, FaTimes } from "react-icons/fa";
import CustomInput from "../common/CustomInput";
import CustomSelect from "../common/CustomSelect";
import ImageUploadModal from "./ImageUploadModal";
import HighlightsModal from "../common/HighlightsModal";
import HighlightsDisplay from "../common/HighlightsDisplay";
import CustomDateTimePicker from "../common/CustomDateTimePicker";
import PhoneNumberField from "./common/PhoneNumberField";
import MobileNumberUpdateModal from "../common/MobileNumberUpdateModal";
import ChangePasswordModal from "../common/ChangePasswordModal";
import { playerService } from "../../services/api";
import toast from "react-hot-toast";

const PlayerProfileForm = ({
  playerProfileData,
  formApiData,
  isEditing,
  isLoading,
  isSaving,
  error,
  onApiInputChange,
  onProfileImageChange,
  onSave,
  onCancel,
  onEdit,
  calculateAge,
  onMobileUpdate,
}) => {
  const [formData, setFormData] = useState({
    firstName: "Christiano",
    lastName: "Ronaldo",
    height: "5'11ft",
    weight: "80kg",
    foot: "left",
    country: "portugal",
    totalExperience: "1",
    birthday: "",
    age: "24yrs",
  });

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showHighlightsModal, setShowHighlightsModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [profileImage, setProfileImage] = useState(
    formApiData.player_image ||
      defaultImage
  );
  const [uploadedImageBlobUrl, setUploadedImageBlobUrl] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  
  // Highlights state
  const [highlights, setHighlights] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(true);
  const [highlightsError, setHighlightsError] = useState(null);
  
  // Fetch player highlights
  useEffect(() => {
    const fetchHighlights = async () => {
      setHighlightsLoading(true);
      setHighlightsError(null);
      try {
        const response = await playerService.getPlayerHighlights();
        if (response.success) {
          setHighlights(response.data);
        } else {
          setHighlightsError(response.error || "Failed to load highlights");
        }
      } catch (error) {
        console.error("Error fetching highlights:", error);
        setHighlightsError("Failed to load highlights");
      } finally {
        setHighlightsLoading(false);
      }
    };
    
    fetchHighlights();
  }, []);

  // Update profile image when API data changes
  useEffect(() => {
    // If formApiData.player_image is a string URL (from API), use it and clear blob URL
    if (formApiData.player_image && typeof formApiData.player_image === "string" && !formApiData.player_image.startsWith("blob:")) {
      // Cleanup blob URL if exists (image was saved)
      if (uploadedImageBlobUrl) {
        URL.revokeObjectURL(uploadedImageBlobUrl);
        setUploadedImageBlobUrl(null);
      }
      setProfileImage(formApiData.player_image);
      // Clear loading state when API URL is received (after save)
      setIsUploadingImage(false);
    } else if (formApiData.player_image instanceof File) {
      // If it's a File object, create blob URL for preview
      if (!uploadedImageBlobUrl) {
        const blobUrl = URL.createObjectURL(formApiData.player_image);
        setProfileImage(blobUrl);
        setUploadedImageBlobUrl(blobUrl);
      }
      // Keep loading state - will be cleared when save completes (when API URL is received)
    } else if (!formApiData.player_image && !uploadedImageBlobUrl) {
      // Fallback to placeholder if no image
      setProfileImage(defaultImage);
      setIsUploadingImage(false);
    }
  }, [formApiData.player_image, uploadedImageBlobUrl]);

  // Cleanup blob URL when component unmounts or when image is saved
  useEffect(() => {
    return () => {
      if (uploadedImageBlobUrl) {
        URL.revokeObjectURL(uploadedImageBlobUrl);
      }
    };
  }, [uploadedImageBlobUrl]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleMobileUpdateSuccess = (newMobile) => {
    if (onApiInputChange) {
      onApiInputChange("phone", newMobile);
    }
    if (typeof onMobileUpdate === "function") {
      onMobileUpdate(newMobile);
    }
  };

  // Handle saving updated highlights
  const handleSaveHighlights = async (updatedHighlights) => {
    setHighlights(updatedHighlights);
    toast.success("Highlights updated successfully");
  };

  const handleProfileImageUpload = () => {
    if (isUploadingImage) {
      return; // Don't allow opening modal while uploading
    }
    setShowUploadModal(true);
  };

  const handleProfileImageRemove = () => {
    // Cleanup blob URL if exists
    if (uploadedImageBlobUrl) {
      URL.revokeObjectURL(uploadedImageBlobUrl);
      setUploadedImageBlobUrl(null);
    }
    setProfileImage(defaultImage);
    // Reset the image in the parent component
    onProfileImageChange(null);
  };

  const toggleClaimRepresentation = () => {
    setFormData((prev) => ({
      ...prev,
      claimRepresentation: !prev.claimRepresentation,
    }));
  };

  const handleImageUpload = (croppedImage) => {
    console.log("handleImageUpload called with:", typeof croppedImage, croppedImage?.substring?.(0, 50));
    
    if (!croppedImage) {
      console.error("No image provided to handleImageUpload");
      setIsUploadingImage(false);
      return;
    }
    
    setIsUploadingImage(true);
    
    // Cleanup previous blob URL if exists
    if (uploadedImageBlobUrl) {
      URL.revokeObjectURL(uploadedImageBlobUrl);
    }
    
    // Set preview immediately with the blob URL
    if (typeof croppedImage === "string" && croppedImage.startsWith("blob:")) {
      setProfileImage(croppedImage);
      setUploadedImageBlobUrl(croppedImage);
    } else if (typeof croppedImage === "string" && croppedImage.startsWith("data:")) {
      // Convert data URL to blob URL for consistency
      fetch(croppedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          setProfileImage(blobUrl);
          setUploadedImageBlobUrl(blobUrl);
        })
        .catch((err) => {
          console.error("Error converting data URL to blob:", err);
          setProfileImage(croppedImage);
          setIsUploadingImage(false);
          toast.error("Failed to process image");
        });
    } else {
      setProfileImage(croppedImage);
    }
    
    // Close modal after setting preview
    setShowUploadModal(false);

    // Convert base64 or blob URL to File object for API submission
    if (typeof croppedImage === "string" && (croppedImage.startsWith("data:") || croppedImage.startsWith("blob:"))) {
      fetch(croppedImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], "profile_image.png", {
            type: blob.type || "image/png",
          });
          console.log("Created File object:", file.name, file.size, file.type);
          onProfileImageChange(file);
          // Keep loading state - will be cleared when save completes (in useEffect)
        })
        .catch((err) => {
          console.error("Error converting image to File:", err);
          setIsUploadingImage(false);
          toast.error("Failed to process image. Please try again.");
        });
    } else if (croppedImage instanceof File) {
      // Already a File object - create blob URL for preview
      const blobUrl = URL.createObjectURL(croppedImage);
      setProfileImage(blobUrl);
      setUploadedImageBlobUrl(blobUrl);
      console.log("Already a File object:", croppedImage.name);
      onProfileImageChange(croppedImage);
      // Keep loading state - will be cleared when save completes (in useEffect)
    } else {
      console.log("Unknown image format, passing as-is:", typeof croppedImage);
      onProfileImageChange(croppedImage);
      setIsUploadingImage(false);
    }
  };

  // Options for dropdowns
  const footOptions = [
    { value: "left", label: "Left" },
    { value: "right", label: "Right" },
    { value: "both", label: "Both" },
  ];

  const countryOptions = [
    { value: "portugal", label: "Portugal" },
    { value: "spain", label: "Spain" },
    { value: "england", label: "England" },
    { value: "france", label: "France" },
    { value: "germany", label: "Germany" },
  ];

  const experienceOptions = [
    { value: "1", label: "1" },
    { value: "2", label: "2" },
    { value: "3", label: "3" },
    { value: "4", label: "4" },
    { value: "5", label: "5" },
  ];

  // Calculate age from date of birth
  const age = formApiData.date_of_birth
    ? calculateAge(formApiData.date_of_birth)
    : "";

  // Render loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="text-gray-500 text-lg">Loading profile...</span>
      </div>
    );
  }

  return (
    <>
      <div className="sm:px-14 px-4 py-6 space-y-8">
        {/* Personal Details Section */}
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <div className="flex justify-between items-center gap-3 mb-6 flex-col sm:flex-row">
            <h2 className="text-[11px] font-bold text-[#3C3C3C] self-start">
              Personal details
            </h2>

           <div className="self-end">
             {!isEditing ? (
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setShowChangePasswordModal(true)}
                  className="border boder-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
                >
                  Change password
                </button>
                <button
                  onClick={onEdit}
                  className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
                >
                  Edit profile
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={onCancel}
                  disabled={isSaving}
                  className="border boder-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
                >
                  Cancel
                </button>
                <button
                  onClick={onSave}
                  disabled={isSaving}
                  className="bg-[#2283FF] px-4 py-2 text-[#F5F5F5] rounded-lg"
                >
                  {isSaving ? "Saving..." : "Save changes"}
                </button>
              </div>
            )}
           </div>
          </div>

          {/* Error message */}
          {error && <div className="mb-4 text-red-600 text-sm">{error}</div>}

          <div className="flex gap-6 mb-6">
            {/* Profile Picture */}
            <div className="flex-shrink-0 relative">
              <div className="w-24 h-24 rounded-full bg-gray-300 flex items-center justify-center overflow-hidden">
                <img
                  src={profileImage}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
                {/* Loading Overlay */}
                {isUploadingImage && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Profile Picture Actions */}
            {isEditing && (
              <div className="flex gap-6 justify-center items-center">
                <button
                  onClick={handleProfileImageUpload}
                  disabled={isUploadingImage}
                  className="text-blue-600 bg-[#2283FF0D] h-fit px-5 py-2 rounded-lg hover:text-blue-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploadingImage ? "Processing..." : "Upload"}
                </button>
                <button
                  onClick={handleProfileImageRemove}
                  disabled={isUploadingImage}
                  className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Remove
                </button>
              </div>
            )}
          </div>

          {/* Personal Details Form */}
          {/* Row 1: First Name and Last Name */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <CustomInput
              label="Player Name"
              value={formApiData.first_name}
              onChange={(e) =>
                isEditing && onApiInputChange("first_name", e.target.value)
              }
              placeholder="Enter first name"
              className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
              disabled={!isEditing}
            />
          </div>

          {/* Row 2: Email */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <CustomInput
              label="Email address"
              value={formApiData.email}
              onChange={(e) =>
                isEditing && onApiInputChange("email", e.target.value)
              }
              placeholder="Enter email"
              className="!bg-[#F8F9FB] !text-[#1A212FB2] !text-[14px] !placeholder:text-[14px] !font-medium !placeholder:font-medium !placeholder:text-[#1A212F66]"
              disabled={!isEditing}
            />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            <PhoneNumberField
              label="Phone number"
              value={formApiData.phone}
              placeholder="Enter phone number"
              disabled
              showEdit={isEditing}
              onEdit={() => setShowMobileModal(true)}
            />
          </div>
        </div>

        {/* Highlights Section */}
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <div className="flex items-center justify-between mb-4 h-14">
            <div className="flex items-center gap-3">
              <div className="flex flex-col justify-center h-full">
                <h2 className="text-[11px] font-bold text-[#3C3C3C]">
                  Highlights
                </h2>
                {highlightsError && (
                  <p className="text-xs text-red-500 mt-1">{highlightsError}</p>
                )}
              </div>
            </div>

            <button
              onClick={() => setShowHighlightsModal(true)}
              className="flex items-center gap-2 border border-[#E6E6E6] px-4 py-2 rounded-lg text-[#1E1E1E]"
              disabled={highlightsLoading}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.334 1.99955C11.5091 1.82445 11.7169 1.68556 11.9457 1.5908C12.1745 1.49604 12.4197 1.44727 12.6673 1.44727C12.9149 1.44727 13.1601 1.49604 13.3889 1.5908C13.6177 1.68556 13.8256 1.82445 14.0007 1.99955C14.1757 2.17465 14.3146 2.38252 14.4094 2.61129C14.5042 2.84006 14.5529 3.08526 14.5529 3.33288C14.5529 3.58051 14.5042 3.8257 14.4094 4.05448C14.3146 4.28325 14.1757 4.49112 14.0007 4.66622L5.00065 13.6662L1.33398 14.6662L2.33398 10.9996L11.334 1.99955Z"
                  stroke="#2C2C2C"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Edit Highlights
            </button>
          </div>

          {highlightsLoading ? (
            <div className="flex justify-center items-center h-48 bg-[#F8F9FB]">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <HighlightsDisplay
              highlights={highlights}
              showEditButton={false}
            />
          )}
        </div>

        {/* Management & Club Section */}
        <div className="bg-white p-6 border border-[#E6E6E6]">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-[11px] font-bold text-[#3C3C3C]">
              Management & club
            </h2>
          </div>

          {/* Agent and Club Cards */}
          {!playerProfileData?.current_agent && !playerProfileData?.current_club ? (
            <div className="flex flex-col items-center justify-center py-12 px-4">
              <div className="text-center">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <h3 className="text-sm font-medium text-gray-900 mb-1">
                  No management or club information
                </h3>
                <p className="text-sm text-gray-500">
                  You don't have any agent or club associated with your profile yet.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex gap-10 flex-col sm:flex-row items-start">

              {
                !playerProfileData?.current_agent?.name && !playerProfileData?.current_club?.name && (
                  <span className="text-sm text-gray-500">No agent or club associated with your profile yet.</span>
                )
              }
              {/* Agent Card - Only show if agent exists and has name */}
              {playerProfileData?.current_agent?.name && (
                <div className="bg-[#F8F9FB] border border-[#D6DBEA] p-4 rounded-xl w-full sm:w-fit sm:pr-32 sm:pl-10">
                  <div className="flex flex-col justify-center gap-2">
                    <img
                      src={playerProfileData.current_agent.profile_picture_url || "/default_profile.webp"}
                      alt={playerProfileData.current_agent.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <span className="text-[#1A212F] text-[13px] font-bold">Agent Name</span>
                    <span className="font-medium text-[#1A212FB2] text-[13px]">
                      {playerProfileData.current_agent.name}
                    </span>
                  </div>
                </div>
              )}

              {/* Club Card - Only show if club exists and has name */}
              {playerProfileData?.current_club?.name && (
                <div className="bg-[#F8F9FB] border border-[#D6DBEA] p-4 rounded-xl w-full sm:w-fit sm:pr-32 sm:pl-10">
                  <div className="flex flex-col justify-center gap-2">
                    <img
                      src={playerProfileData.current_club.logo || "/default_profile.webp"}
                      alt={playerProfileData.current_club.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <span className="text-[#1A212F] text-[13px] font-bold">
                      Club Name
                    </span>
                    <span className="font-medium text-[#1A212FB2] text-[13px]">
                      {playerProfileData.current_club.name}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <ImageUploadModal
          onClose={() => {
            if (!isUploadingImage) {
              setShowUploadModal(false);
            }
          }}
          onUpload={handleImageUpload}
        />
      )}

      {/* Highlights Modal */}
      <HighlightsModal
        isOpen={showHighlightsModal}
        onClose={() => setShowHighlightsModal(false)}
        highlights={highlights}
        onSave={handleSaveHighlights}
      />

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          onClose={() => setShowChangePasswordModal(false)}
          onSuccess={() => {
            // Could add any success handling here
          }}
        />
      )}

      <MobileNumberUpdateModal
        isOpen={showMobileModal}
        onClose={() => setShowMobileModal(false)}
        initialMobile={formApiData.phone || ""}
        onSuccess={handleMobileUpdateSuccess}
      />
    </>
  );
};

export default PlayerProfileForm;
